import { useMemo, useState } from 'react';

import { Button, Modal, Select, Space, TreeSelect, Typography } from 'antd';
import { Building2 } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Company } from 'models/Company';
import { Role } from 'models/Role';

import ImpersonationAction, {
  ImpersonationPayload,
} from '../../../redux/actions/impersonationActions/Actions';
import { ImpersonationState } from '../../../redux/reducers/ImpersonationReducer';

const { Text } = Typography;

interface ImpersonationModalProps {
  isOpen: boolean;
  onClose: () => void;
  companies: Company[];
  roles: Role[];
}

function ImpersonationModal({
  isOpen,
  onClose,
  companies,
  roles,
}: ImpersonationModalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const currentImpersonation: ImpersonationState = useSelector(
    (state: any) => state.impersonation
  );

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    currentImpersonation.companyId
  );
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>(
    currentImpersonation.roleIds
  );
  const [isSwitching, setIsSwitching] = useState(false);

  // Filtrar roles segun la compania seleccionada
  const filteredRoles = useMemo(() => {
    if (selectedCompanyId === null) return [];
    return roles.filter((r) => r.companyId === selectedCompanyId);
  }, [selectedCompanyId, roles]);

  const handleCompanyChange = (value: number | undefined) => {
    setSelectedCompanyId(value ?? null);
    setSelectedRoleIds([]);
  };

  const applyContext = (payload: ImpersonationPayload) => {
    dispatch(ImpersonationAction.setImpersonation(payload));
    onClose();
    setIsSwitching(true);
    setTimeout(() => {
      window.location.href = '/';
    }, 300);
  };

  const handleApply = () => {
    applyContext({
      companyId: selectedCompanyId,
      roleIds: selectedRoleIds,
    });
  };

  const handleResetCompany = () => {
    applyContext({ companyId: null, roleIds: [] });
  };

  // Agrupar companias por pais para TreeSelect
  const companyTreeData = useMemo(() => {
    const grouped: Record<string, Company[]> = {};
    companies.forEach((company) => {
      const countryName = company.country?.name ?? company.countryCode;
      if (!grouped[countryName]) grouped[countryName] = [];
      grouped[countryName].push(company);
    });
    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([countryName, list]) => ({
        title: countryName,
        value: 'country-' + countryName,
        selectable: false,
        children: list
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((company) => ({
            title: company.name,
            value: company.id,
          })),
      }));
  }, [companies]);

  const roleOptions = filteredRoles.map((r) => ({
    value: r.id,
    label: r.name,
  }));

  return (
    <>
      {isSwitching &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.65)',
              zIndex: 10000,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 16,
            }}
          >
            <Building2 size={40} color="#fff" />
            <Text
              style={{
                color: '#fff',
                fontSize: 18,
                fontWeight: 500,
              }}
            >
              {t('components.layout.impersonation_modal.switching_message')}
            </Text>
          </div>,
          document.body
        )}

      <Modal
        title={
          <Space>
            <Building2 size={16} />
            <span>{t('components.layout.impersonation_modal.title')}</span>
          </Space>
        }
        open={isOpen}
        onCancel={onClose}
        onOk={handleApply}
        okText={t('components.layout.impersonation_modal.buttons.apply')}
        cancelText={t('components.layout.impersonation_modal.buttons.cancel')}
        footer={(_, { OkBtn }) => (
          <Space>
            {currentImpersonation.companyId !== null ? (
              <Button onClick={handleResetCompany}>
                {t(
                  'components.layout.impersonation_modal.buttons.reset_company'
                )}
              </Button>
            ) : (
              <Button onClick={onClose}>
                {t('components.layout.impersonation_modal.buttons.cancel')}
              </Button>
            )}
            <OkBtn />
          </Space>
        )}
        width={480}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size={20}>
          <div>
            <Text
              strong
              style={{ display: 'block', marginBottom: 6, fontSize: 13 }}
            >
              {t('components.layout.impersonation_modal.labels.company')}
            </Text>
            <TreeSelect
              style={{ width: '100%' }}
              placeholder={t(
                'components.layout.impersonation_modal.placeholders.select_company'
              )}
              treeData={companyTreeData}
              value={selectedCompanyId ?? undefined}
              onChange={handleCompanyChange}
              showSearch
              allowClear
              treeDefaultExpandAll
              treeLine
              treeNodeFilterProp="title"
            />
          </div>

          <div>
            <Text
              strong
              style={{ display: 'block', marginBottom: 6, fontSize: 13 }}
            >
              {t('components.layout.impersonation_modal.labels.roles')}
            </Text>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder={
                selectedCompanyId === null
                  ? t(
                      'components.layout.impersonation_modal.placeholders.select_company_first'
                    )
                  : t(
                      'components.layout.impersonation_modal.placeholders.select_roles'
                    )
              }
              options={roleOptions}
              value={selectedRoleIds}
              onChange={(values: number[]) => setSelectedRoleIds(values)}
              disabled={selectedCompanyId === null}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </div>
        </Space>
      </Modal>
    </>
  );
}

export default ImpersonationModal;
